<script setup lang="ts">
import { Network } from '@capacitor/network';

const config = useRuntimeConfig();
const platformStore = usePlatformStore();
const settingsStore = useSettingsStore();
const roleManagerStore = useRoleManagerStore();
const hotkeyManager = useHotkeyManager();
const accountSettingsStore = useAccountSettingsStore();
const { openToast } = useUserMessages();
const { setLocale, locales, t } = useEkI18n();
const { route } = useRouting();
const { beforeEach } = useRouter();

// Using `_accountSettings` here because using `accountSettings` directly before it has been initialized throws an error
const browserTitle = computed(() => accountSettingsStore._accountSettings?.BrowserTitle);
const tabTitle = computed(() => browserTitle.value ? `App ${browserTitle.value}` : 'App');

useUnhead({
  titleTemplate: (titleChunk) => {
    return titleChunk ? `${tabTitle.value} - ${titleChunk}` : tabTitle.value;
  }
});

useI18nToDateFns();
provide(promptBiometricKey, ref(true));
beforeEach((to) => {
  if (roleManagerStore.isRoutePermitted(to.path) === false) {

    openToast(t('toast.noPermissionToPage'), ToastType.ERROR);

    return false;
  }
});

document.body.classList.add(`platform-${platformStore.platform}`);

watch(() => settingsStore.settings.language, async (value) => {
  await setLocale(value as 'da-DK' | 'en-GB' | 'de-DE');
}, { immediate: true });

watch(() => settingsStore.settings.theme, (value) => {
  let useDarkmode = false;
  let useHighContrast = false;

  switch (value) {
    case ThemeSetting.system:
      useDarkmode = window.matchMedia('(prefers-color-scheme: dark)').matches;
      useHighContrast = false;
      break;
    case ThemeSetting.light:
      useDarkmode = false;
      useHighContrast = false;
      break;
    case ThemeSetting.dark:
      useDarkmode = true;
      useHighContrast = false;
      break;
    case ThemeSetting.darkHighContrast:
      useDarkmode = true;
      useHighContrast = true;
      break;
  }

  document.body.classList.toggle('dark', useDarkmode);
  document.body.classList.toggle('darkHighContrast', useHighContrast);
}, { immediate: true });

let i = 0;

if (config.public.showDevPages === true) {
  window.addEventListener('keypress', (event: KeyboardEvent) => {
    if (event.target instanceof Element && event.target.matches('body') === false) {
      return;
    }

    switch (event.key) {
      case 'å':
      case 'Å':
      {
        i++;
        const a = locales.value[i % locales.value.length].language;

        if (a !== undefined) {
          settingsStore.set('language', a);
        }

        event.preventDefault();
        break;
      }
    }
  });
}

let offlineToast: (HTMLIonToastElement | null) = null;
let connectedToast: (HTMLIonToastElement | null) = null;
Network.addListener('networkStatusChange', async (status) => {
  if (route.name == 'worksheets-worksheetId-qa-schemaId') {
    if (status.connected === false && offlineToast == null) {
      if (connectedToast && await connectedToast.dismiss()) {
        connectedToast.remove();
        connectedToast = null;
      }

      offlineToast = await toastController.create({
        message: t('alert.notConnected'),
        color: 'danger',
        position: 'top',
        buttons: [
          {
            text: 'x',
            role: 'cancel'
          }
        ]
      });
      await offlineToast.present();
    }

    if (status.connected === true) {
      if (offlineToast && await offlineToast.dismiss()) {
        offlineToast.remove();
      }

      if (offlineToast != null) {
        offlineToast = null;
        connectedToast = await toastController.create({
          message: t('alert.reconnected'),
          color: 'success',
          position: 'top',
          duration: 3000
        });
        await connectedToast.present();
      }
    }
  }
});

if (platformStore.isWeb) {
  window.addEventListener('keydown', hotkeyManager.handleKeydown);
}
</script>

<template>
  <DebugOverlay v-if="config.public.showDevPages" />
  <IonApp>
    <IonSplitPane 
      content-id="main-content" 
      when="false"
    >
      <LayoutSideMenu v-if="route.path !== '/'" />
      <IonRouterOutlet 
        id="main-content" 
        :animated="false" 
      />
    </IonSplitPane>
  </IonApp>
</template>

<style scoped>
ion-split-pane {
  --side-width: 16em;
}
</style>
